/**
 * Catálogo declarativo dos seeds (Central de Seeds).
 *
 * Cada item descreve, de forma 100% declarativa, como o composable `useSeeds()`
 * deve interagir com o backend. Nenhuma lógica de negócio vive aqui — apenas
 * metadados de apresentação e descritores de endpoints.
 *
 * Estrutura de cada seed:
 *   {
 *     chave, nome, descricao,
 *     grupo: 'MASTER' | 'ORGANIZACAO',   // eixo de navegação (toggle)
 *     escopo: 'MASTER' | 'TENANT' | 'ORGANIZACAO',  // escopo de gating
 *     versao,
 *     preRequisito,
 *     icone,
 *     endpoints: { status?, executar, exportar?, reconciliar?, verificar? },
 *     parametrosExtras?
 *   }
 *
 * Descritor de endpoint:
 *   status    -> { metodo, url, params, campoChave }
 *   executar  -> { metodo, url, corpo?, params? }
 *   exportar  -> { metodo, url, params? }
 *   reconciliar -> { metodo, url, corpo?, params? }
 *   verificar -> { metodo, url, params? }
 */

// ============================ Escopos (gating) ============================
// MASTER: escreve no banco Master (configuração global/sistema) — sem gating.
// TENANT: escreve no banco Tenant no escopo do cliente — exige empresa.
// ORGANIZACAO: escreve no banco Tenant com IdOrganizacao — exige empresa + organização.
export const ESCOPO_TENANT = 'TENANT'
export const ESCOPO_MASTER = 'MASTER'
export const ESCOPO_ORGANIZACAO = 'ORGANIZACAO'

// ============================ Grupos (toggle) ============================
// MASTER: configuração do Master + operação de sistema (MIGRATIONS).
// ORGANIZACAO: dados de negócio gravados com IdOrganizacao no banco Tenant.
export const GRUPO_MASTER = 'MASTER'
export const GRUPO_ORGANIZACAO = 'ORGANIZACAO'

// Tokens dinâmicos resolvidos em tempo de execução pelo composable useSeeds()
export const TOKEN_EMPRESA = '$empresa'
export const TOKEN_ORGANIZACAO = '$organizacao'
export const TOKEN_FORCAR_DOC = '$forcarDoc'

// Tipos de corpo (payload) suportados pelo composable
export const CORPO_EMPRESAS = '$empresas'
export const CORPO_EMPRESAS_OU_TODAS = '$empresasOuTodas'
export const CORPO_SINCRONIZAR_LISTAS = '$sincronizarListas'
export const CORPO_TRIBUTO_CONFIGURACAO = '$tributoConfiguracao'

/**
 * Rótulos visuais por estado de seed.
 */
export const ROTULOS_STATUS = {
  concluido: { texto: 'Concluído', classe: 'text-success', icone: 'bi-check-circle-fill' },
  pendente: { texto: 'Pendente', classe: 'text-warning', icone: 'bi-circle' },
  erro: { texto: 'Erro', classe: 'text-danger', icone: 'bi-x-circle-fill' },
  executando: { texto: 'Executando', classe: 'text-info', icone: 'bi-arrow-repeat' },
  nao_rastreado: { texto: 'Não rastreado', classe: 'text-body-secondary', icone: 'bi-dash-circle' }
}

export function rotuloStatus (chave) {
  return ROTULOS_STATUS[chave] || ROTULOS_STATUS.nao_rastreado
}

export const SEEDS_CATALOG = [
  // ============================= GRUPO MASTER =============================
  // Seeds que escrevem no banco Master (configuração global) + operação de sistema.
  {
    chave: 'REGRAS_ACESSO',
    nome: 'Regras de Acesso',
    descricao: 'Sincroniza as regras de acesso no Master DB com base no código-fonte (reconciliação total).',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: null,
    preRequisito: null,
    icone: 'bi-shield-lock-fill',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-regras-acesso'
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-regras-acesso'
      }
    }
  },
  {
    chave: 'DICIONARIO',
    nome: 'Dicionário de Metadados',
    descricao: 'Sincroniza mapeamentos de tabelas, campos e listas de lookup no Master DB com base no código-fonte.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: null,
    preRequisito: null,
    icone: 'bi-bookmarks-fill',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-dicionario-metadados'
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-dicionario-metadados'
      }
    }
  },
  {
    chave: 'LISTAS_SISTEMA',
    nome: 'Listas do Sistema',
    descricao: 'Sincroniza cabeçalhos e itens das listas do sistema no Master DB com base no código-fonte.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: null,
    preRequisito: null,
    icone: 'bi-list-ul',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-listas-sistema'
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-listas-sistema'
      }
    }
  },
  {
    chave: 'DOCUMENTACAO',
    nome: 'Documentação do Sistema',
    descricao: 'Sincroniza a documentação das telas no Master DB com base nas classes C#.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: '2.5',
    preRequisito: null,
    icone: 'bi-file-text-fill',
    camposConfig: [
      { chave: 'forcarAtualizacao', rotuloCurto: 'Forçar', label: 'Sobrescrever documentação integralmente (forçar)?', tipo: 'booleano', padrao: false, dica: 'Ignora a comparação campo a campo e sobrescreve todos os documentos.' }
    ],
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-documentacao-sistema',
        params: { forcarAtualizacao: TOKEN_FORCAR_DOC }
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-documentacao-sistema'
      },
      verificar: {
        metodo: 'post',
        url: '/Setup/verificar-alteracoes-documentacao'
      }
    }
  },
  {
    chave: 'TEMPLATES_IA',
    nome: 'Templates de Configurações de IA',
    descricao: 'Sincroniza os templates de configurações de IA no Master DB com base no código-fonte.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: '2.4',
    preRequisito: null,
    icone: 'bi-robot',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-templates-configuracoes-ia'
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-templates-configuracoes-ia'
      }
    }
  },
  {
    chave: 'RELATORIO_TEMPLATES',
    nome: 'Templates de Relatório',
    descricao: 'Sincroniza os templates de relatório no Master DB com base no código-fonte.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: '2.4',
    preRequisito: null,
    icone: 'bi-file-earmark-pdf',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-relatorio-templates'
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-relatorio-templates'
      }
    }
  },
  {
    chave: 'TIPOS_IMAGEM',
    nome: 'Configurações de Imagens',
    descricao: 'Sincroniza os tipos de imagem configurados (logo, banner, marca d\'água) no Master DB.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: '2.3',
    preRequisito: null,
    icone: 'bi-images',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-tipos-imagem-config'
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-tipos-imagem-config'
      }
    }
  },
  {
    chave: 'CONFIG_IA_EMPRESA',
    nome: 'Configurações de IA nas Empresas',
    descricao: 'Aplica os templates de Configurações de IA nas empresas (cria ClienteIntegracaoAi por template ativo).',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_TENANT,
    versao: null,
    preRequisito: 'TEMPLATES_IA',
    icone: 'bi-robot',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-configuracoes-ia',
        corpo: CORPO_EMPRESAS_OU_TODAS
      }
    }
  },
  {
    chave: 'MIGRATIONS',
    nome: 'Migrations dos Tenants',
    descricao: 'Aplica as migrations pendentes do AppDbContextTenant em todos os bancos dos clientes.',
    grupo: GRUPO_MASTER,
    escopo: ESCOPO_MASTER,
    versao: null,
    preRequisito: null,
    icone: 'bi-database-fill-up',
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/AplicarMigrationsTodosTenants'
      }
    }
  },

  // ============================= GRUPO ORGANIZAÇÃO =============================
  // Seeds que escrevem no banco Tenant com IdOrganizacao (exigem empresa + organização).
  {
    chave: 'PLANO_CONTAS',
    nome: 'Template Plano de Contas de Categorias Financeiras',
    descricao: 'Cria o template do plano de contas de categorias financeiras (82 categorias hierárquicas em 6 grupos) no tenant da empresa ativa.',
    grupo: GRUPO_ORGANIZACAO,
    escopo: ESCOPO_ORGANIZACAO,
    versao: null,
    preRequisito: null,
    icone: 'bi-diagram-3-fill',
    endpoints: {
      status: {
        metodo: 'get',
        url: '/Setup/status-seed-categorias',
        params: { empresaId: TOKEN_EMPRESA, organizacaoId: TOKEN_ORGANIZACAO },
        campoChave: 'planoExiste'
      },
      executar: {
        metodo: 'post',
        url: '/Setup/seed-categorias-financeiras',
        corpo: CORPO_EMPRESAS
      },
      reconciliar: {
        metodo: 'post',
        url: '/Setup/reconciliar-categorias-financeiras',
        corpo: CORPO_EMPRESAS
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-categorias-financeiras',
        params: { empresaId: TOKEN_EMPRESA }
      }
    }
  },
  {
    chave: 'DRE_LAYOUT',
    nome: 'Layout de DRE Padrão',
    descricao: 'Cria o layout de DRE gerencial padrão (5 seções) vinculado às categorias do plano de contas.',
    grupo: GRUPO_ORGANIZACAO,
    escopo: ESCOPO_ORGANIZACAO,
    versao: null,
    preRequisito: 'PLANO_CONTAS',
    icone: 'bi-file-bar-graph',
    endpoints: {
      status: {
        metodo: 'get',
        url: '/Setup/status-seed-dre-layout',
        params: { empresaId: TOKEN_EMPRESA, organizacaoId: TOKEN_ORGANIZACAO },
        campoChave: 'layoutExiste'
      },
      executar: {
        metodo: 'post',
        url: '/Setup/seed-dre-layout',
        corpo: CORPO_EMPRESAS
      },
      exportar: {
        metodo: 'post',
        url: '/Setup/exportar-seed-dre-layout',
        params: { empresaId: TOKEN_EMPRESA }
      }
    }
  },
  {
    chave: 'LISTAS_TENANT',
    nome: 'Sincronizar Listas dos Tenants',
    descricao: 'Distribui as listas oficiais, inativa itens órfãos e atualiza descrições nos clientes.',
    grupo: GRUPO_ORGANIZACAO,
    escopo: ESCOPO_ORGANIZACAO,
    versao: null,
    preRequisito: null,
    icone: 'bi-cloud-arrow-down-fill',
    camposConfig: [
      { chave: 'atualizarTextos', rotuloCurto: 'Textos', label: 'Atualizar textos e descrições das listas?', tipo: 'booleano', padrao: true },
      { chave: 'atualizarStatus', rotuloCurto: 'Status', label: 'Sobrescrever status (Ativo/Inativo) das listas?', tipo: 'booleano', padrao: false }
    ],
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/SincronizarListasCliente',
        corpo: CORPO_SINCRONIZAR_LISTAS
      }
    }
  },
  {
    chave: 'TRIBUTO_CONFIGURACAO',
    nome: 'Configuração de Tributos (Adequação Fiscal)',
    descricao: 'Aplica um bloco de configuração de tributos (Fase 2026/2027/2028+) delimitando todos os registros anteriores da empresa.',
    grupo: GRUPO_ORGANIZACAO,
    escopo: ESCOPO_ORGANIZACAO,
    versao: null,
    preRequisito: null,
    icone: 'bi-receipt',
    camposConfig: [
      {
        chave: 'fase',
        rotuloCurto: 'Fase',
        label: 'Fase da legislação',
        tipo: 'select',
        padrao: 'A',
        opcoes: [
          { id: 'A', label: 'Fase 2026' },
          { id: 'B', label: 'Fase 2027' },
          { id: 'C', label: 'Fase 2028+' }
        ],
        dica: 'Bloco completo e alternativo. Fase 2026: todos em Faturamento. Fase 2027: CBS/IS em Liquidação. Fase 2028+: CBS/IS/IBS em Liquidação. Ao aplicar, os registros anteriores são delimitados automaticamente.'
      },
      {
        chave: 'dataInicioVigencia',
        rotuloCurto: 'Início',
        label: 'Data de início da vigência',
        tipo: 'data',
        padrao: null,
        dica: 'Vigência dos tributos do bloco. Deve ser posterior ao início de qualquer tributo já cadastrado.'
      },
      {
        chave: 'dataFimVigencia',
        rotuloCurto: 'Fim',
        label: 'Data de fim da vigência (opcional)',
        tipo: 'data',
        padrao: null,
        dica: 'Em branco = vigência indeterminada.'
      }
    ],
    endpoints: {
      executar: {
        metodo: 'post',
        url: '/Setup/seed-tributo-configuracao',
        corpo: CORPO_TRIBUTO_CONFIGURACAO
      }
    }
  }
]

export function obterSeed (chave) {
  return SEEDS_CATALOG.find(s => s.chave === chave) || null
}

export function seedTemConfig (seed) {
  return Array.isArray(seed?.camposConfig) && seed.camposConfig.length > 0
}

export function seedsDoGrupo (grupo) {
  return SEEDS_CATALOG.filter(s => s.grupo === grupo)
}
